import React, { useState, useEffect, useCallback } from 'react';
import { 
  Inbox, Bell, AtSign, UserCheck, Pin, X, RefreshCw, Filter, 
  ChevronRight, MessageCircle, Clock, CheckCircle, ExternalLink,
  Send, MoreVertical, AlertTriangle, Calendar
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

/**
 * CommentInbox - Global inbox for comment activity
 * Shows mentions, assigned items, and pinned comments in user's scope
 */
const CommentInbox = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    entityType: null,
    days: 30
  });
  const [showFilters, setShowFilters] = useState(false);

  const tenantId = user?.tenant?.id || user?.tenant_id;

  const tabs = [
    { id: 'all', label: 'All', icon: Inbox },
    { id: 'mentions', label: 'Mentions', icon: AtSign },
    { id: 'assigned', label: 'Assigned', icon: UserCheck },
    { id: 'pinned', label: 'Pinned', icon: Pin }
  ];

  const entityTypes = [
    'portfolio', 'program', 'project', 'epic', 'feature', 
    'story', 'task', 'issue', 'risk', 'decision', 'dependency'
  ];

  const loadInbox = useCallback(async () => {
    if (!tenantId || !user?.id) return;
    setLoading(true);
    try {
      const res = await api.getCommentInbox({
        tenant_id: tenantId,
        user_id: user.id,
        tab: activeTab,
        entity_type: filters.entityType,
        days: filters.days,
        limit: 50
      });
      setItems(res.data.items || []);
    } catch (err) {
      console.error('Failed to load inbox:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, user?.id, activeTab, filters]);

  const loadCounts = useCallback(async () => {
    if (!tenantId || !user?.id) return;
    try {
      const res = await api.getCommentInboxCounts({
        tenant_id: tenantId,
        user_id: user.id
      });
      setCounts(res.data.counts || {});
    } catch (err) {
      console.error('Failed to load inbox counts:', err);
    }
  }, [tenantId, user?.id]);

  useEffect(() => {
    if (isOpen) {
      loadInbox();
      loadCounts();
    }
  }, [isOpen, loadInbox, loadCounts]);

  const handleOpenEntity = (item) => {
    // Navigate based on entity type
    const type = item.entity_type?.toLowerCase();
    const id = item.entity_id;
    
    // Map entity types to routes
    const routes = {
      portfolio: `/dashboard/pmo`,
      program: `/dashboard/pmo`,
      project: `/projects/${id}`,
      epic: `/epics`,
      feature: `/features`,
      story: `/stories`,
      task: `/tasks`,
      issue: `/issues`,
      risk: `/issues`,
      decision: `/decisions`,
      dependency: `/dependencies`
    };

    const route = routes[type] || '/dashboard/executive';
    navigate(route);
    onClose();
  };

  const handleMarkResolved = async (item) => {
    try {
      await api.updateComment(item.id, {
        body: { resolved: true },
        params: { tenant_id: tenantId, user_id: user?.id }
      });
      loadInbox();
    } catch (err) {
      console.error('Failed to mark resolved:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'mention': return <AtSign size={14} className="text-blue-500" />;
      case 'assigned': return <UserCheck size={14} className="text-purple-500" />;
      case 'pinned': return <Pin size={14} className="text-amber-500" />;
      default: return <MessageCircle size={14} className="text-gray-500" />;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} />
      
      {/* Inbox Panel */}
      <div className="fixed right-4 top-16 w-96 max-h-[80vh] bg-white rounded-xl shadow-2xl border border-gray-200 z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 bg-[var(--clr-surface-subtle)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Inbox size={20} className="text-[var(--clr-brand-primary)]" />
              <h2 className="font-semibold text-gray-900">My Inbox</h2>
              {counts.all > 0 && (
                <span className="text-xs bg-[var(--clr-brand-primary)] text-white px-2 py-0.5 rounded-full">
                  {counts.all}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button 
                onClick={() => { loadInbox(); loadCounts(); }}
                className="p-1.5 hover:bg-gray-100 rounded-lg"
                title="Refresh"
              >
                <RefreshCw size={16} className="text-gray-500" />
              </button>
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`p-1.5 rounded-lg ${showFilters ? 'bg-gray-100' : 'hover:bg-gray-100'}`}
                title="Filters"
              >
                <Filter size={16} className="text-gray-500" />
              </button>
              <button 
                onClick={onClose}
                className="p-1.5 hover:bg-gray-100 rounded-lg"
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-3">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[var(--clr-brand-primary)] text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon size={14} />
                {tab.label}
                {counts[tab.id] > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                    activeTab === tab.id
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {counts[tab.id]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Context</label>
                <select
                  value={filters.entityType || ''}
                  onChange={(e) => setFilters({ ...filters, entityType: e.target.value || null })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                >
                  <option value="">All types</option>
                  {entityTypes.map(type => (
                    <option key={type} value={type} className="capitalize">
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Time</label>
                <select
                  value={filters.days}
                  onChange={(e) => setFilters({ ...filters, days: parseInt(e.target.value) })}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Items List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw size={24} className="text-gray-400 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Inbox size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">No items in your inbox</p>
              <p className="text-gray-400 text-sm mt-1">
                {activeTab === 'mentions' && 'Comments where you are @mentioned will appear here'}
                {activeTab === 'assigned' && 'Comments assigned to you will appear here'}
                {activeTab === 'pinned' && 'Pinned comments in your scope will appear here'}
                {activeTab === 'all' && 'Your comment activity will appear here'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map(item => (
                <div 
                  key={item.id}
                  className={`p-4 hover:bg-gray-50 transition-colors ${item.resolved ? 'opacity-60' : ''}`}
                >
                  {/* Item Header */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {getCategoryIcon(item.inbox_category)}
                      <span className="text-xs font-medium text-gray-500 capitalize">
                        {item.entity_type}
                      </span>
                      {item.resolved && (
                        <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                          Resolved
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400">{formatDate(item.created_at)}</span>
                  </div>

                  {/* Item Content */}
                  <div className="mt-2">
                    <p className="text-sm text-gray-800 line-clamp-2">{item.message}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="w-5 h-5 rounded-full bg-[var(--clr-brand-primary)] flex items-center justify-center text-white text-xs">
                        {(item.created_by_name || 'U').charAt(0).toUpperCase()}
                      </div>
                      <span className="text-xs text-gray-500">{item.created_by_name}</span>
                      {item.created_by_role && (
                        <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded capitalize">
                          {item.created_by_role.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => handleOpenEntity(item)}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-[var(--clr-brand-primary)] hover:bg-[var(--clr-brand-primary)]/10 rounded transition-colors"
                    >
                      <ExternalLink size={12} /> Open
                    </button>
                    {!item.resolved && (
                      <button
                        onClick={() => handleMarkResolved(item)}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-600 hover:bg-green-50 rounded transition-colors"
                      >
                        <CheckCircle size={12} /> Mark done
                      </button>
                    )}
                    {item.inbox_category === 'assigned' && (
                      <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded flex items-center gap-1">
                        <UserCheck size={12} /> Assigned to you
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500 text-center">
            Showing {items.length} items from the last {filters.days} days
          </p>
        </div>
      </div>
    </>
  );
};

export default CommentInbox;
